// src/pages/cadastros/PessoasPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination
} from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaExclamationTriangle, FaSave } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface Pessoa {
  PES_CODIGO: number;
  PES_NOME: string;
  PES_ENDERECO: string;
  PES_FONE: string;
  PES_CPF: string;
  PES_RG: string;
}

interface PessoaFormValues {
  PES_NOME: string;
  PES_ENDERECO: string;
  PES_FONE: string;
  PES_CPF: string;
  PES_RG: string;
}

const ITEMS_PER_PAGE = 20;

const validationSchema = Yup.object({
  PES_NOME: Yup.string().required('Nome é obrigatório').max(100),
  PES_ENDERECO: Yup.string().max(200),
  PES_FONE: Yup.string().max(20),
  PES_CPF: Yup.string().max(20),
  PES_RG: Yup.string().max(20),
});

const emptyForm: PessoaFormValues = {
  PES_NOME: '', PES_ENDERECO: '', PES_FONE: '', PES_CPF: '', PES_RG: '',
};

const PessoasPage: React.FC = () => {
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentPessoa, setCurrentPessoa] = useState<Pessoa | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pessoaToDelete, setPessoaToDelete] = useState<Pessoa | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchPessoas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = searchTerm.trim()
        ? `/pessoas/nome/${encodeURIComponent(searchTerm.trim())}`
        : '/pessoas';
      const response = await api.get(url);
      setPessoas(response.data);
      setCurrentPage(1);
    } catch (err) {
      setError('Não foi possível carregar as pessoas. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(fetchPessoas, 400);
    return () => clearTimeout(timer);
  }, [fetchPessoas]);

  const { sortedData, sortKey, sortDir, handleSort } = useSortable<Pessoa>(pessoas, 'PES_NOME');
  const paginatedPessoas = sortedData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(pessoas.length / ITEMS_PER_PAGE);

  const handleOpenCreate = () => {
    setModalMode('create');
    setCurrentPessoa(null);
    setShowModal(true);
  };

  const handleOpenEdit = (pessoa: Pessoa) => {
    setModalMode('edit');
    setCurrentPessoa(pessoa);
    setShowModal(true);
  };

  const handleDeleteClick = (pessoa: Pessoa) => {
    setPessoaToDelete(pessoa);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (
    values: PessoaFormValues,
    { resetForm }: FormikHelpers<PessoaFormValues>
  ) => {
    try {
      setFormSubmitting(true);
      if (modalMode === 'create') {
        await api.post('/pessoas', values);
      } else {
        await api.put(`/pessoas/${currentPessoa!.PES_CODIGO}`, values);
      }
      setShowModal(false);
      resetForm();
      fetchPessoas();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pessoaToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/pessoas/${pessoaToDelete.PES_CODIGO}`);
      setShowDeleteModal(false);
      setPessoaToDelete(null);
      fetchPessoas();
    } catch {
      setError('Não foi possível excluir. Tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Cadastro de Pessoas</h1>
        <Button variant="primary" onClick={handleOpenCreate}>
          {FaPlus({ className: "me-2" })} Nova Pessoa
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {FaExclamationTriangle({ className: "me-2" })}{error}
        </Alert>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {pessoas.length}</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando pessoas...</p>
            </div>
          ) : pessoas.length === 0 ? (
            <Alert variant="info">Nenhuma pessoa encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Pessoa> label="Código" sortKey="PES_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Nome" sortKey="PES_NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Endereço" sortKey="PES_ENDERECO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Fone" sortKey="PES_FONE" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="CPF" sortKey="PES_CPF" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="RG" sortKey="PES_RG" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPessoas.map(p => (
                      <tr key={p.PES_CODIGO}>
                        <td>{p.PES_CODIGO}</td>
                        <td>{p.PES_NOME}</td>
                        <td>{p.PES_ENDERECO}</td>
                        <td>{p.PES_FONE}</td>
                        <td>{p.PES_CPF}</td>
                        <td>{p.PES_RG}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleOpenEdit(p)} title="Editar">
                            {FaEdit({})}
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(p)} title="Excluir">
                            {FaTrash({})}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Criar/Editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'create' ? 'Nova Pessoa' : 'Editar Pessoa'}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={currentPessoa
            ? { PES_NOME: currentPessoa.PES_NOME, PES_ENDERECO: currentPessoa.PES_ENDERECO, PES_FONE: currentPessoa.PES_FONE, PES_CPF: currentPessoa.PES_CPF, PES_RG: currentPessoa.PES_RG }
            : emptyForm}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formSubmit }) => (
            <Form onSubmit={formSubmit}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nome *</Form.Label>
                  <Form.Control name="PES_NOME" value={values.PES_NOME} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.PES_NOME && !!errors.PES_NOME} />
                  <Form.Control.Feedback type="invalid">{errors.PES_NOME}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Endereço</Form.Label>
                  <Form.Control name="PES_ENDERECO" value={values.PES_ENDERECO} onChange={handleChange} onBlur={handleBlur} />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fone</Form.Label>
                      <Form.Control name="PES_FONE" value={values.PES_FONE} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>CPF</Form.Label>
                      <Form.Control name="PES_CPF" value={values.PES_CPF} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>RG</Form.Label>
                  <Form.Control name="PES_RG" value={values.PES_RG} onChange={handleChange} onBlur={handleBlur} />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button variant="primary" type="submit" disabled={formSubmitting}>
                  {formSubmitting ? 'Salvando...' : <>{FaSave({ className: "me-2" })}Salvar</>}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Excluir */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: "text-danger mb-3" })}
          <p>Excluir a pessoa <strong>{pessoaToDelete?.PES_NOME}</strong>?</p>
          <p className="text-danger small">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PessoasPage;
